import { Injectable } from "@angular/core";
import { environment } from "environments/environment";
import { Observable, OperatorFunction, Subject } from "rxjs";
import { map } from "rxjs/operators";
import { HttpService } from "services/http.service";
import { WorkforcePlanSolicitationModel } from "services/models/workforceplan/workforceplan-solicitation.model";
import { ServiceBase } from "services/servicebase";
import _ from 'lodash'
import { WorkforcePlanSolicitationDetailsModel } from "services/models/workforceplan/workforceplan-solicitation-details.model";
import { WorkforcePlanModel } from "services/models/workforceplan";
import {
    ConvertAptitudeTrainingPreview,
    ConvertAptitudeTrainingResult,
} from "services/models/workforceplan/convert-aptitude-training.model";
import { Response } from "services/models/response";
import { QueryParams } from "services/models/odata/queryParams";

type StateScreenActionsResponse = {
    shouldRefetchCountSolicition?: boolean;
};

type StateWorkforceplanDataResponse = {
    data?: WorkforcePlanModel;
    form?: any;
};


@Injectable()
export class WorkforceplanMovementRequestService extends ServiceBase<WorkforcePlanSolicitationModel> {

    createEntity(input: any): WorkforcePlanSolicitationModel {
        throw new Error("Method not implemented.");
    }

    private stateActionsSubject = new Subject<StateScreenActionsResponse>();
    public stateScreenActions$ = this.stateActionsSubject.asObservable();

    private stateWorkforceplanSubject = new Subject<StateWorkforceplanDataResponse>();
    public stateWorkforceplanData$ = this.stateWorkforceplanSubject.asObservable();


    constructor(http: HttpService) {
        super("WorkForcePlan", http, environment.apiUrl);
    }

    emitEventUpdateStateScreenActions(data: StateScreenActionsResponse) {
        this.stateActionsSubject.next(data);
    }

    emitEventUpdateStateWorkforceplanData(data: StateWorkforceplanDataResponse) {
        this.stateWorkforceplanSubject.next(data);
    }

    getCountWorkforcePlanPendingSolicitation(wfpId: string): Observable<any> {
        return this.http.get(`${this.endpoint}/${wfpId}/movement-request/pending`, null, null, null, true).pipe(
            map((res) => {
                return res.body
            })
        );
    }

    getFilterableMovementTypes(wfpId: string): Observable<string[]> {
        return this.http
            .get(`${this.endpoint}/${wfpId}/movement-request/filterable-types`, null, null, null, true)
            .pipe(
                map((res) => (res.body ?? []).map((item: { movementType?: unknown }) => {
                    const movementType = item?.movementType;
                    if (typeof movementType === 'string') {
                        return movementType;
                    }
                    if (typeof movementType === 'number') {
                        return `${movementType}`;
                    }
                    return `${movementType ?? ''}`;
                }).filter((type: string) => !!type))
            );
    }

    getWorkforcePlanSolicitation(id: string, params: QueryParams): Observable<Response<WorkforcePlanSolicitationModel[]>> {
        const query = this.createBuildQuery(params);
        return this.http
        .get(`${this.endpoint}/${id}/movement-request/odata${query}`, null, null, null, true)
        .pipe(
            map((res) => {
                const response = new Response<WorkforcePlanSolicitationModel[]>();

                response.data = res.body.map((item: any) => {
                    const created = WorkforcePlanSolicitationModel.create(item);
                    const rejectedIndex = created.steps.findIndex(f => f.status === 'REJECTED');
                    if (rejectedIndex >= 0)
                        created.steps = created.steps.slice(0, rejectedIndex + 1)

                    return created;
                });

                response.count = res.headers.get("x-count");
                return response;
            })
        );
    }


    getWorkforcePlanSolicitationById(wfpId: string, solicitationId: string): Observable<WorkforcePlanSolicitationModel[]> {
        return this.http.get(`${this.endpoint}/${wfpId}/movement-request/odata?$filter=id eq ${solicitationId}&$orderBy=CreationDate desc`, null, null, null, true).pipe(
            map((res) => {
                return res.body.map((item: any) => {
                    const created = WorkforcePlanSolicitationModel.create(item);

                    const rejectedIndex = created.steps.findIndex(f => f.status === 'REJECTED');
                    if (rejectedIndex >= 0)
                        created.steps = created.steps.slice(0, rejectedIndex + 1)

                    return created;
                })
            })
        );
    }

    getWorkforcePlanSolicitationPermission(approvalScopeItemId: string): Observable<{ canExecute: boolean; canUpdate: boolean; }> {
        return this.http.get(`${this.endpoint}/movement-request/${approvalScopeItemId}/permission`, null, null, null, true).pipe(
            map((res) => {
                return res.body
            })
        );
    }

    postWorkforcePlanSolicitationUpdateStatus(movementRequestId: string, data: any) {
        return this.http.post(`${this.endpoint}/movement-request/${movementRequestId}/status`, data, null, null, true, true)
    }

    getWorkforcePlanSolicitationDetails(movementRequestId: string, approvalScopeItemId: string): Observable<WorkforcePlanSolicitationDetailsModel> {
        return this.http.get(`${this.endpoint}/movement-request/${movementRequestId}/details/${approvalScopeItemId}`, null, null, null, true).pipe(
            map((res) => WorkforcePlanSolicitationDetailsModel.create(res.body))
        );
    }

    downloadMovementRequestFile(movementRequestId: string, fileId: string) {
        return this.http.get(
            `${this.endpoint}/movement-request/${movementRequestId}/files/${fileId}/download`,
            null,
            null,
            null,
            true
        );
    }

    previewMovementRequestFile(movementRequestId: string, fileId: string) {
        return this.http.get(
            `${this.endpoint}/movement-request/${movementRequestId}/files/${fileId}/preview`,
            null,
            null,
            null,
            true
        );
    }

    getConvertAptitudeTrainingPreview(movementRequestId: string): Observable<ConvertAptitudeTrainingPreview> {
        return this.http
            .get(
                `${this.endpoint}/movement-request/${movementRequestId}/convert-to-internal-training/preview`,
                null,
                null,
                null,
                true
            )
            .pipe(map((res) => res.body as ConvertAptitudeTrainingPreview));
    }

    cancelMovementRequest(movementRequestId: string, reason: string) {
        return this.http.post(`${this.endpoint}/movement-request/${movementRequestId}/cancel`, { reason }, null, null, true, true);
    }

    convertAptitudeTrainingToInternalTraining(movementRequestId: string): Observable<ConvertAptitudeTrainingResult> {
        return this.http
            .post(
                `${this.endpoint}/movement-request/${movementRequestId}/convert-to-internal-training`,
                {},
                null,
                null,
                true,
                true
            )
            .pipe(map((res) => (res?.body ?? res) as ConvertAptitudeTrainingResult));
    }

}
